/**
 * Lightweight hook to check YaaS status on wallet connect.
 * Uses the same endpoint and query key as useYaasWalletData so React Query
 * shares the cache — no duplicate API calls when both hooks are active.
 */
import { captureWalletEvent, eventId, type EventOwner } from "@shared/analytics/walletEvents";
import { walletJourney } from "@shared/analytics/walletJourney";
import { useWalletStore } from "@shared/stores/walletStore";
import { useTokenBalancesData } from "@shared/wallet/hooks/useTokenBalances";
import { useSessionStore, useWallet } from "@solomon-apps/shared";
import type { ParticipantAccountStatus } from "@solomon-apps/shared";
import { useEffect, useRef } from "react";

import { useWalletView } from "./useWalletView";

export function useYaasStatus(): void {
    const { connected, isWalletReady, publicKey } = useWallet();
    const { setYaasStatus, resetYaasStatus } = useSessionStore();
    const address = publicKey?.toString();
    const balances = useTokenBalancesData();
    const contextCheck = useRef<{
        owner: EventOwner;
        address: string;
        id: string;
        started: number;
        settled: boolean;
    } | null>(null);
    const prevAddressRef = useRef<string | null>(null);

    // Reset YaaS status whenever wallet disconnects or switches address.
    // This prevents stale status from a previous wallet from being reused.
    useEffect(() => {
        if (!connected || !address) {
            prevAddressRef.current = null;
            resetYaasStatus();
            return;
        }

        if (prevAddressRef.current !== address) {
            prevAddressRef.current = address;
            resetYaasStatus();
        }
    }, [connected, address, resetYaasStatus]);

    // Shares query key ["yaas", "wallet", address] with useYaasWalletData
    // so that React Query deduplicates requests to the same endpoint.
    const { data, isSuccess, isError, isFetching } = useWalletView(address, isWalletReady && !!address);

    useEffect(() => {
        if (!connected || !address) {
            contextCheck.current = null;
            return;
        }
        if (contextCheck.current?.address !== address) {
            contextCheck.current = {
                owner: walletJourney.owner(),
                address,
                id: eventId(),
                started: performance.now(),
                settled: false
            };
            captureWalletEvent(
                "wallet:context_load_started",
                { check_id: contextCheck.current.id, trigger: "initial" },
                contextCheck.current.owner
            );
        }
        const check = contextCheck.current;
        if (
            check.settled ||
            balances.isLoading ||
            isFetching ||
            (!isSuccess && !isError) ||
            (!balances.error && useWalletStore.getState().lastFetchedAddress !== address)
        )
            return;
        check.settled = true;
        const properties = { check_id: check.id, duration_ms: performance.now() - check.started };
        if (isError || balances.error)
            captureWalletEvent(
                "wallet:context_load_failed",
                {
                    ...properties,
                    failed_component: isError && balances.error ? "both" : isError ? "account" : "balances",
                    error_code: "context_load_failed",
                    error_category: "network"
                },
                check.owner
            );
        else
            captureWalletEvent(
                "wallet:context_load_completed",
                {
                    ...properties,
                    has_usdv: balances.balances.usdv?.gt(0),
                    has_stables: !!(balances.balances.usdc?.gt(0) || balances.balances.usdt?.gt(0)),
                    account_status:
                        data?.account?.status === "Active"
                            ? "active"
                            : data?.account?.status === "Pending"
                              ? "pending"
                              : data?.account?.status === "Suspended"
                                ? "suspended"
                                : data?.account?.status === "Blacklisted"
                                  ? "blacklisted"
                                  : data?.account?.status === "Closed"
                                    ? "closed"
                                    : "none"
                },
                check.owner
            );
    }, [
        connected,
        address,
        balances.isLoading,
        balances.error,
        balances.balances,
        data,
        isSuccess,
        isError,
        isFetching
    ]);

    // Update session store when status is fetched
    useEffect(() => {
        if (isSuccess) {
            const status = data?.account?.status as ParticipantAccountStatus | undefined;
            setYaasStatus(status ?? null);
        }
    }, [isSuccess, data, setYaasStatus]);

    // Handle error case — mark status as loaded (null) so the access check
    // can proceed instead of blocking the connecting modal indefinitely.
    useEffect(() => {
        if (isError) {
            setYaasStatus(null);
        }
    }, [isError, setYaasStatus]);
}
